import { useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import {
  TOOLS,
  TOOLS_SETTINGS_KEY,
  defaultToolsConfig,
  mergeToolsConfig,
  type ToolsConfig,
} from "@/config/toolsRegistry";

// På servern hämtas inställningen vid varje sidrendering (rotens loader).
// Håll den i minnet en minut per Worker-instans i stället för att fråga
// databasen varje gång.
const SERVER_TTL_MS = 60_000;
let serverCache: { at: number; value: ToolsConfig } | null = null;

export const fetchToolsConfig = async (): Promise<ToolsConfig> => {
  const onServer = typeof window === "undefined";
  if (onServer && serverCache && Date.now() - serverCache.at < SERVER_TTL_MS) return serverCache.value;
  const value = await queryToolsConfig();
  if (onServer) serverCache = { at: Date.now(), value };
  return value;
};

const queryToolsConfig = async (): Promise<ToolsConfig> => {
  const { data } = await supabase
    .from("site_settings")
    .select("value")
    .eq("key", TOOLS_SETTINGS_KEY)
    .maybeSingle();
  return mergeToolsConfig(data?.value);
};

export const saveToolsConfig = async (config: ToolsConfig) => {
  const { error } = await supabase.from("site_settings").upsert({
    key: TOOLS_SETTINGS_KEY,
    value: config as never,
    updated_at: new Date().toISOString(),
  });
  if (error) throw error;
};

/** Är sökvägen ett avstängt verktyg (eller undersida till ett)? */
export const isPathDisabled = (config: ToolsConfig, path: string) => {
  const clean = (path.split("?")[0] ?? path).replace(/\/+$/, "") || "/";
  const tool = TOOLS.find((t) => clean === t.path || clean.startsWith(`${t.path}/`));
  return tool ? config[tool.id]?.enabled === false : false;
};

const rootApi = getRouteApi("__root__");

export const useToolsConfig = () => {
  // Rotruttens loader hämtar konfigurationen redan på servern, så avstängda
  // sidor och länkar till dem aldrig finns i serverns HTML (crawlers hittar
  // dem annars).
  const ssrConfig = rootApi.useLoaderData({
    select: (d: { toolsConfig?: ToolsConfig } | undefined) => d?.toolsConfig,
  }) as ToolsConfig | undefined;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["tools-config"],
    queryFn: fetchToolsConfig,
    staleTime: 60_000,
    initialData: ssrConfig,
  });

  const config = data ?? ssrConfig ?? defaultToolsConfig();

  return {
    config,
    loading: isLoading && !ssrConfig,
    refetch,
    isToolEnabled: (toolId: string) => config[toolId]?.enabled !== false,
    isPathEnabled: (path: string) => !isPathDisabled(config, path),
    isFeatureEnabled: (toolId: string, featureId: string) => {
      const tool = config[toolId];
      if (!tool || tool.enabled === false) return false;
      return tool.features?.[featureId] !== false;
    },
  };
};
